import type { SectionStyle, SectionTone } from "../section-style.fields";
import { BOLD_CONTAINER_CLASS } from "../ui/bold-container";
import type { BaseComponentProps } from '../../types/puck';
import { BoldStepList } from "./bold-step-list";
import { DARK_TONE } from "./process-step";
import { DisplayHeading } from "../ui/display-heading";
import type { ProcessStep } from "./process-step";
import { cn } from "../../lib/utils";

interface ProcessStepsProps extends BaseComponentProps {
  heading: string;
  subheading?: string;
  steps: ProcessStep[];
  layout: "vertical" | "horizontal" | "numbered";
  /** Bold: the heading in capitals and each step's number in big type. */
  variant?: SectionStyle;
  /** Dark: the steps on a dark band in the theme's primary, with light type. */
  tone?: SectionTone;
  accentColor?: string;
  backgroundColor?: string;
}

/**
 * Whether an optional setting holds a value.
 * @param {string} [value] The setting.
 * @returns {boolean} True for a non-empty string.
 */
const isSet = (value?: string): value is string => (value ?? "") !== "";

const ProcessSteps: React.FC<ProcessStepsProps> = ({
  heading,
  subheading,
  steps,
  layout = "vertical",
  variant = "standard",
  tone = "light",
  accentColor,
  backgroundColor,
  id,
  className,
}) => {
  const surfaceRadiusStyle = { borderRadius: "var(--jobsite-radius)" } as const,
    isBold = variant === "bold",
    isDark = tone === "dark",
    // The band's colour, unless the owner painted the section a colour of their own.
    bandClass = isDark ? "jobsite:bg-primary" : "jobsite:bg-background",

    renderVerticalLayout = () => (
      <div className="jobsite:relative">
        {/* Vertical line connector */}
        <div className={cn("jobsite:absolute jobsite:left-6 jobsite:top-0 jobsite:bottom-0 jobsite:w-0.5 jobsite:bg-border jobsite:hidden jobsite:md:block", isDark && DARK_TONE.line)} />

        <div className="jobsite:space-y-8">
          {steps.map((step, idx) => (
            <div key={idx} className="jobsite:relative jobsite:flex jobsite:gap-6">
              {/* Step number circle */}
              <div
                className={cn(
                  "jobsite:relative jobsite:z-10 jobsite:flex jobsite:items-center jobsite:justify-center",
                  "jobsite:w-12 jobsite:h-12 jobsite:rounded-full jobsite:font-bold jobsite:text-lg",
                  "jobsite:bg-primary jobsite:text-primary-foreground jobsite:shrink-0",
                  isDark && "jobsite:bg-primary-foreground jobsite:text-primary",
                )}
                style={accentColor ? { backgroundColor: accentColor } : undefined}
              >
                {step.step}
              </div>

              {/* Content */}
              <div className="jobsite:pt-1">
                <h3 className={cn("jobsite:text-xl jobsite:mb-2 jobsite:font-heading jobsite:text-foreground", isDark && DARK_TONE.title)}>
                  {step.title}
                </h3>
                <p className={cn("jobsite:text-base jobsite:text-muted-foreground jobsite:leading-relaxed", isDark && DARK_TONE.body)}>
                  {step.description}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    ),

    renderHorizontalLayout = () => (
      <div className="jobsite:relative">
        {/* Single connector line behind all circles — hidden below lg or for single steps */}
        {steps.length > 1 && (
          <div
            className={cn("jobsite:hidden jobsite:lg:block jobsite:absolute jobsite:h-0.5 jobsite:bg-border", isDark && DARK_TONE.line)}
            style={{
              left: `calc(${100 / steps.length / 2}% + 1.5rem)`,
              right: `calc(${100 / steps.length / 2}% + 1.5rem)`,
              top: "1.5rem",
            }}
          />
        )}

        <div className="jobsite:grid jobsite:grid-cols-1 jobsite:sm:grid-cols-2 jobsite:lg:grid-cols-3 jobsite:gap-10 jobsite:lg:gap-16">
          {steps.map((step, idx) => (
            <div key={idx} className="jobsite:relative jobsite:text-center jobsite:group">
              {/* Step number - minimal circle */}
              <div
                className={cn(
                  "jobsite:relative jobsite:z-10 jobsite:mx-auto jobsite:flex jobsite:items-center jobsite:justify-center",
                  "jobsite:w-12 jobsite:h-12 jobsite:rounded-full jobsite:font-semibold jobsite:text-base jobsite:mb-5",
                  "jobsite:border-2 jobsite:border-primary jobsite:text-primary jobsite:bg-background",
                  "jobsite:transition-all jobsite:duration-300",
                  "jobsite:group-hover:bg-primary jobsite:group-hover:text-primary-foreground",
                  isDark &&
                    "jobsite:border-primary-foreground jobsite:text-primary-foreground jobsite:bg-primary jobsite:group-hover:bg-primary-foreground jobsite:group-hover:text-primary",
                )}
                style={accentColor ? { borderColor: accentColor, color: accentColor } : undefined}
              >
                {step.step}
              </div>

              {/* Content */}
              <h3 className={cn("jobsite:text-base jobsite:mb-2 jobsite:font-heading jobsite:text-foreground", isDark && DARK_TONE.title)}>
                {step.title}
              </h3>
              <p className={cn("jobsite:text-sm jobsite:text-muted-foreground jobsite:leading-relaxed jobsite:max-w-xs jobsite:mx-auto", isDark && DARK_TONE.body)}>
                {step.description}
              </p>
            </div>
          ))}
        </div>
      </div>
    ),

    renderNumberedLayout = () => (
      <div className="jobsite:space-y-6">
        {steps.map((step, idx) => (
          <div
            key={idx}
            className={cn(
              "jobsite:flex jobsite:gap-4 jobsite:p-6",
              "jobsite:bg-background jobsite:border jobsite:border-border/70",
              isDark && DARK_TONE.card,
            )}
            style={surfaceRadiusStyle}
          >
            {/* Large step number */}
            <div
              className={cn(
                "jobsite:text-4xl jobsite:font-bold jobsite:text-primary jobsite:shrink-0 jobsite:w-12",
                isDark && DARK_TONE.title,
              )}
              style={accentColor ? { color: accentColor } : undefined}
            >
              {String(step.step).padStart(2, "0")}
            </div>

            {/* Content */}
            <div>
              <h3 className={cn("jobsite:text-xl jobsite:mb-2 jobsite:font-heading jobsite:text-card-foreground", isDark && DARK_TONE.title)}>
                {step.title}
              </h3>
              <p className={cn("jobsite:text-base jobsite:text-muted-foreground jobsite:leading-relaxed", isDark && DARK_TONE.body)}>
                {step.description}
              </p>
            </div>
          </div>
        ))}
      </div>
    );

  if (isBold) {
    return (
      <section
        id={id}
        className={cn("jobsite:py-16 jobsite:md:py-24", isSet(backgroundColor) ? "" : bandClass, className)}
        style={isSet(backgroundColor) ? { backgroundColor } : undefined}
      >
        <div className={BOLD_CONTAINER_CLASS}>
          <DisplayHeading heading={heading} subheading={subheading} onDark={isDark} />
          <BoldStepList steps={steps} layout={layout} accentColor={accentColor} onDark={isDark} />
        </div>
      </section>
    );
  }

  return (
    <section
      id={id}
      className={cn(
        "jobsite:py-16 jobsite:md:py-24",
        backgroundColor ? "" : bandClass,
        className,
      )}
      style={backgroundColor ? { backgroundColor } : undefined}
    >
      <div className="jobsite:container jobsite:mx-auto jobsite:px-4">
        {/* Header */}
        <div className="jobsite:text-center jobsite:mb-12 jobsite:max-w-3xl jobsite:mx-auto">
          <h2 className={cn("jobsite:text-3xl jobsite:sm:text-4xl jobsite:mb-4 jobsite:font-heading jobsite:tracking-tight jobsite:text-balance jobsite:text-foreground", isDark && DARK_TONE.title)}>
            {heading}
          </h2>
          {subheading && (
            <p className={cn("jobsite:text-lg jobsite:text-pretty jobsite:text-muted-foreground", isDark && DARK_TONE.body)}>{subheading}</p>
          )}
        </div>

        {/* Steps */}
        <div className="jobsite:max-w-4xl jobsite:mx-auto">
          {layout === "vertical" && renderVerticalLayout()}
          {layout === "horizontal" && renderHorizontalLayout()}
          {layout === "numbered" && renderNumberedLayout()}
        </div>
      </div>
    </section>
  );
};

export type { ProcessStepsProps };
export { ProcessSteps };
